// Gracefully prevent benign Vite HMR WebSocket disconnect messages from triggering unhandled rejection banners or error overlays
(function () {
  function isBenignWebSocketMessage(str: any) {
    if (!str || typeof str !== 'string') return false;
    const lower = str.toLowerCase();
    return (
      lower.indexOf('websocket') !== -1 ||
      lower.indexOf('failed to connect to websocket') !== -1 ||
      lower.indexOf('closed without opened') !== -1 ||
      lower.indexOf('closed before the connection') !== -1 ||
      (lower.indexOf('vite') !== -1 && lower.indexOf('ws') !== -1)
    );
  }

  if (typeof window !== 'undefined') {
    // Intercept console.error to prevent benign Vite dev-server HMR connection failures from polluting Lighthouse console audit
    const origConsoleError = console.error;
    console.error = function (...args: any[]) {
      const combined = args.map(a => typeof a === 'string' ? a : (a?.message || '')).join(' ');
      if (isBenignWebSocketMessage(combined)) return;
      origConsoleError.apply(console, args);
    };

    window.addEventListener('unhandledrejection', (event) => {
      if (event && event.reason) {
        const msg = typeof event.reason === 'string' ? event.reason : (event.reason.message || String(event.reason));
        if (isBenignWebSocketMessage(msg)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
    }, true);

    window.addEventListener('error', (event) => {
      if (event && event.message && isBenignWebSocketMessage(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }
})();

import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { initWebMcp } from './lib/webMcp.ts';
import './index.css';

// Initialize WebMCP tools for in-browser AI agent interaction
if (typeof window !== 'undefined') {
  initWebMcp();
}

// Defer non-critical web font downloads until after initial render to avoid chaining critical requests
if (typeof window !== 'undefined') {
  const loadFonts = () => {
    import('./fonts.css').catch(() => {});
  };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(loadFonts, { timeout: 1500 });
  } else {
    setTimeout(loadFonts, 60);
  }
}

// Manage Service Worker registration
if ('serviceWorker' in navigator) {
  const isDev = Boolean((import.meta as any).env?.DEV);
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  if (isDev || isInIframe) {
    // In development mode or inside the AI Studio live preview iframe, unregister existing service workers to ensure fresh previews
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister().catch(() => {});
      }
    }).catch(() => {});
  } else {
    // In production standalone mode, register Service Worker silently for offline PWA support
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Silent catch to prevent console logging
      });
    });
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

